import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import warehouseService, { CreateWarehouseDTO } from '../../services/warehouseService';
import '../../styles/ImsTheme.css';
import './WarehouseCreatePage.css';
import { ArrowLeft, Save } from 'lucide-react';
import { parseApiError } from '../../utils/errorUtils';

// Max length constants for validation
const MAX_LENGTHS = {
  code: 10,
  name: 30,
  location: 100,
  address: 200,
  city: 50,
  state: 50,
  postalCode: 20,
};

interface FieldErrors {
  code?: string;
  name?: string;
  location?: string;
}

const WarehouseCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<CreateWarehouseDTO>({
    code: '',
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Enforce max length by truncating
    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Clear field error when user starts typing
    setFieldErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate Code
    if (!formData.code.trim()) {
      errors.code = 'Warehouse code is required';
      isValid = false;
    } else if (formData.code.length > MAX_LENGTHS.code) {
      errors.code = `Warehouse code cannot exceed ${MAX_LENGTHS.code} characters`;
      isValid = false;
    }

    // Validate Name
    if (!formData.name.trim()) {
      errors.name = 'Warehouse name is required';
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = `Warehouse name cannot exceed ${MAX_LENGTHS.name} characters`;
      isValid = false;
    }

    // Validate Location
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
      isValid = false;
    } else if (formData.location.length > MAX_LENGTHS.location) {
      errors.location = `Location cannot exceed ${MAX_LENGTHS.location} characters`;
      isValid = false;
    }

    setFieldErrors(errors);

    // Display error message in error div if validation fails
    if (!isValid) {
      setError('Please correct the errors below before saving.');
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await warehouseService.createWarehouse(formData);
      navigate('/organization/warehouses', {
        state: { message: t('warehouse.createSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('warehouse.failedToCreateWarehouse')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/organization/warehouses')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('warehouse.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/organization/warehouses')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          {/* Form Card */}
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('warehouse.details')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.code')} <span className="required">*</span>
                      <span className="char-counter">{formData.code.length}/{MAX_LENGTHS.code}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.code')}
                      maxLength={MAX_LENGTHS.code}
                    />
                    {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    {!fieldErrors.code && <small className="form-helper">e.g., WH001, WH002</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.warehouseName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.warehouseName')}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">e.g., Main Warehouse, Secondary Storage</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.location')} <span className="required">*</span>
                      <span className="char-counter">{formData.location.length}/{MAX_LENGTHS.location}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.location ? 'is-invalid' : ''}`}
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.location')}
                      maxLength={MAX_LENGTHS.location}
                    />
                    {fieldErrors.location && <small className="form-error">{fieldErrors.location}</small>}
                    {!fieldErrors.location && <small className="form-helper">Primary location of the warehouse</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.address')}
                      <span className="char-counter">{formData.address.length}/{MAX_LENGTHS.address}</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.address')}
                      maxLength={MAX_LENGTHS.address}
                    />
                    <small className="form-helper">Street address (optional)</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.city')}
                        <span className="char-counter">{formData.city.length}/{MAX_LENGTHS.city}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.city')}
                        maxLength={MAX_LENGTHS.city}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.state')}
                        <span className="char-counter">{formData.state.length}/{MAX_LENGTHS.state}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.state')}
                        maxLength={MAX_LENGTHS.state}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.postalCode')}
                        <span className="char-counter">{formData.postalCode.length}/{MAX_LENGTHS.postalCode}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.postalCode')}
                        maxLength={MAX_LENGTHS.postalCode}
                      />
                    </div>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('common.info')}</h3>
              </div>
              <div className="create-card-body">
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p>
                    <strong>{t('common.info')}:</strong>
                  </p>
                  <ul style={{ paddingLeft: '16px', margin: '8px 0' }}>
                    <li>Warehouse code must be unique</li>
                    <li>Use clear, descriptive names</li>
                    <li>Location helps identify primary area</li>
                    <li>Address details are optional but recommended</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseCreatePage;
